import { addDoc, collection, doc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Phone / home session timer. Records only who ran the session, which patient
 * (an id, never a name), and when it started and ended. No audio, no notes.
 *
 * firestore.rules makes these readable by the provider and admins/directors
 * only, stamps times from the server clock, and forbids edits and deletes.
 */
export interface SessionTiming {
  id: string;
  providerId: string;
  patientId: string; // '' when not linked to a patient
  startedAt: Date;
  endedAt: Date | null;
  durationSeconds: number | null;
}

const COLLECTION = 'session_timings';

const toDate = (v: any): Date | null => (v?.toDate ? v.toDate() : null);

function fromDoc(id: string, data: any): SessionTiming {
  const startedAt = toDate(data.startedAt) || new Date();
  const endedAt = toDate(data.endedAt);
  return {
    id,
    providerId: data.providerId,
    patientId: data.patientId || '',
    startedAt,
    endedAt,
    durationSeconds: endedAt ? durationSeconds(startedAt, endedAt) : null,
  };
}

export function durationSeconds(startedAt: Date, endedAt: Date): number {
  return Math.max(0, Math.round((endedAt.getTime() - startedAt.getTime()) / 1000));
}

export function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export async function startSession(providerId: string, practiceOwnerId: string, patientId: string): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    providerId,
    patientId,
    practiceOwnerId,
    startedAt: serverTimestamp(),
    endedAt: null,
  });
  return ref.id;
}

export async function stopSession(timingId: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, timingId), { endedAt: serverTimestamp() });
}

/** The provider's session that was started but never stopped, if any. */
export async function getOpenSession(providerId: string): Promise<SessionTiming | null> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('providerId', '==', providerId), where('endedAt', '==', null), limit(1))
  );
  return snap.empty ? null : fromDoc(snap.docs[0].id, snap.docs[0].data());
}

export async function getRecentSessions(providerId: string, count = 10): Promise<SessionTiming[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('providerId', '==', providerId), orderBy('startedAt', 'desc'), limit(count))
  );
  return snap.docs.map((d) => fromDoc(d.id, d.data())).filter((s) => s.endedAt !== null);
}
